import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';
import { ArrowLeft, Mail, KeyRound, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // 'request' or 'verify'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axiosInstance.post('/auth/forgot-password', { email });
      toast.success('Reset-Code wurde an Ihre E-Mail gesendet!');
      setStep('verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden des Codes');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      toast.error('Bitte geben Sie zuerst Ihre E-Mail-Adresse ein.');
      return;
    }
    setMagicLoading(true);
    try {
      await axiosInstance.post('/auth/magic-link/request', { email });
      toast.success('Magic-Link wurde an Ihre E-Mail gesendet! Schauen Sie auch im Spam-Ordner nach.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden des Magic-Links');
    } finally {
      setMagicLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      await axiosInstance.post('/auth/reset-password', {
        email,
        code,
        new_password: newPassword
      });
      toast.success('Passwort erfolgreich zurückgesetzt!');
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Link to="/login">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Zurück
                </Button>
              </Link>
            </div>
            <CardTitle className="text-2xl">Passwort vergessen?</CardTitle>
            <CardDescription>
              {step === 'request' 
                ? 'Geben Sie Ihre E-Mail-Adresse ein, um einen Reset-Code zu erhalten'
                : 'Geben Sie den Code aus Ihrer E-Mail und Ihr neues Passwort ein'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {step === 'request' ? (
              <form onSubmit={handleRequestCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail-Adresse</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="ihre@email.de"
                      className="pl-10"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={loading}
                  style={{ 
                    backgroundColor: '#C4A084',
                    color: 'white'
                  }}
                  data-testid="forgot-password-request-code-btn"
                >
                  {loading ? 'Wird gesendet...' : 'Code anfordern'}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-[#C4A084] text-[#8B7355] hover:bg-[#F5F0EB] mt-3"
                  disabled={magicLoading || !email}
                  onClick={handleMagicLink}
                  data-testid="forgot-password-magic-link-btn"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {magicLoading ? 'Wird gesendet...' : 'Magic-Link'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">6-stelliger Code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="code"
                      type="text"
                      placeholder="123456"
                      className="pl-10 text-center text-2xl tracking-widest"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Code wurde an {email} gesendet
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">Neues Passwort</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Mindestens 6 Zeichen"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Passwort wiederholen"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={loading}
                    style={{ 
                      backgroundColor: '#C4A084',
                      color: 'white'
                    }}
                  >
                    {loading ? 'Wird zurückgesetzt...' : 'Passwort zurücksetzen'}
                  </Button>
                  
                  <Button 
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setStep('request')}
                  >
                    Neuen Code anfordern
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default ForgotPassword;
